import { assertNever } from "@fluentui/react";
import { useMemo, useReducer, useState } from "react";
import { ReducerFunc, isFunction } from "@excentone/spfx-utilities";
import { CounterHook, ICounterHookOperations, ICounterHookOptions } from "./useCounter.types";

const reducer: ReducerFunc<number, keyof Omit<ICounterHookOperations, 'setMax' | 'reset'>> = (prev, command) => {
  if (!command)
    throw new ReferenceError('command is empty.');

  switch (command.type) {
    case 'set': return command.params;
    case 'increment': return prev + 1;
    case 'decrement': return prev - 1;
    default: assertNever(command.type);
  }
};

export const useCounter = (optionsOrInitialValue?: ICounterHookOptions | number): CounterHook => {
  const [options, setOptions] = useState<ICounterHookOptions>(
    () => {
      if (typeof optionsOrInitialValue === 'number')
        return {
          initialValue: optionsOrInitialValue,
          defaultValue: optionsOrInitialValue
        };

      return {
        initialValue: 0,
        defaultValue: 0,
        ...optionsOrInitialValue
      };
    }
  );

  const [count, dispatch] = useReducer(reducer, options.initialValue);

  const operations = useMemo(
    (): ICounterHookOperations => ({
      set: value => dispatch({ type: 'set', params: value }),
      setMax: setValue => setOptions(prev => ({
        ...prev,
        maxValue: isFunction(setValue)
          ? setValue(prev.maxValue)
          : setValue
      })),
      reset: () => dispatch({ type: 'set', params: options.defaultValue }),
      increment: () => !options.maxValue || count <= options.maxValue
        ? dispatch({ type: 'increment' })
        : void 0,
      decrement: () => !options.maxValue || count <= options.maxValue
        ? dispatch({ type: 'decrement' })
        : void 0,
    }),
    [options]
  );

  return [count, operations] as const;
};